import React from 'react'

import { Box, ModalBody, ModalClose, ModalFooter, ModalHeader } from '@gousto-internal/citrus-react'
import { useSelector } from 'react-redux'

import { MenuRecipe } from '@library/menu-service/queries/menuApiMenuRecipe/types'

import { RecipeCoreId, RecipeUUID } from 'reducers/menu-recipes/recipes'
import { DetailEquipment } from 'routes/Menu/components/Detail/DetailEquipment'
import { SectionPanel } from 'routes/Menu/components/Detail/SectionPanel'
import { getNumPortions } from 'selectors/basket'

import { RecipeAttributes, RecipeQuickStats } from './Attributes'
import { RecipeDescription } from './Description'
import { DetailAddRecipe } from './DetailAddRecipe'
import { DetailAllergenIngredients } from './DetailAllergenIngredients'
import { DetailError } from './DetailError'
import { DetailIngredients } from './DetailIngredients'
import { DetailPerPortion } from './DetailPerPortion'
import { RecipeImage } from './Image'
import { RecipeOptions } from './Options'
import { Title } from './Title'

type DetailProps = {
  id: RecipeCoreId
  title: string
  error: boolean
  loading: boolean
  recipe: MenuRecipe
  reload: () => void
  onClose: () => void
  position?: string
}

export const DetailContent = ({
  id: coreRecipeId,
  title,
  error,
  loading,
  recipe,
  reload,
  onClose,
  position = '0',
}: DetailProps) => {
  const numPortions = useSelector(getNumPortions)

  return (
    <>
      <ModalClose onClose={onClose} />
      <ModalHeader>
        <Title data-testing="recipeDetailTitle">{title}</Title>
      </ModalHeader>
      <ModalBody>
        <Box>
          <RecipeImage foodBrand={recipe.food_brand} images={recipe.images} title={title} />
          <SectionPanel>
            <RecipeOptions recipeUuid={recipe.id} options={recipe.options} />
            <RecipeQuickStats
              prepTime={recipe.prep_time}
              kcalPerPortion={recipe.nutritional_information?.per_portion?.energy_kcal}
              rating={recipe.rating}
              loading={loading}
            />
            <RecipeDescription description={recipe.description} loading={loading} />
            <RecipeAttributes
              dietaryClaims={recipe.dietary_claims}
              equipments={recipe.equipment}
              shelfLife={recipe.shelf_life}
              fiveADay={recipe.five_a_day}
              loading={loading}
            />
          </SectionPanel>
          {error ? (
            <DetailError reload={reload} />
          ) : (
            <>
              <DetailIngredients loading={loading} recipe={recipe} basics={recipe.basics} />
              <DetailEquipment equipment={recipe.equipment} onClose={onClose} loading={loading} />
              <DetailPerPortion loading={loading} recipe={recipe} numPortions={numPortions} />
              <DetailAllergenIngredients recipe={recipe} loading={loading} />
            </>
          )}
        </Box>
      </ModalBody>
      <ModalFooter>
        <DetailAddRecipe
          id={recipe.id as RecipeUUID}
          coreId={coreRecipeId}
          surcharge={recipe.surcharge?.price_per_portion}
          position={position}
          isChefPrepared={recipe.is_chef_prepared}
        />
      </ModalFooter>
    </>
  )
}
